import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useState } from 'react';
import { useMaintenanceMode } from '../contexts/MaintenanceContext';
import BackendApiService from "../services/BackendApiService";
import { getAccessToken } from '../utils/accessTokenStore';
import { buildAdoPrUrl } from '../utils/utils';

import Link from '@mui/material/Link';
import TableHead from '@mui/material/TableHead';
import { ApiResource } from '@shared/interfaces/ApiInterfaces';
import { PrDescriptionMetaDataModuleEntryEnriched } from '@shared/interfaces/CommonInterfaces';
import RequestStatusChip from './RequestStatusChip';
import ResourceContextMenu from './ResourceContextMenu';
import './UserRequests.css';
import { Link as RouterLink } from 'react-router-dom';

const PAGE_SIZE = 20;

type UserRequestsProps = {
  status?: string;
  sinceDays?: number;
  resourceFilter?: string;
  showAllResourcesFilter?: boolean;
  windowed?: boolean;
};

type UiModule = Pick<PrDescriptionMetaDataModuleEntryEnriched, "platform" | "name" | "title"> & {
  count: number
};

type UiResource = ApiResource & {
  id: string;
  platform?: string;
  name?: string;
  modulesUi: UiModule[]
};

function UserRequests(props: UserRequestsProps) {
  const [accessToken] = useState(() => {
    return getAccessToken();
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [userRequests, setUserRequests] = useState<UiResource[]>([]);
  const [visibleData, setVisibleData] = useState<UiResource[]>([]);
  const [showActionId, setShowActionId] = useState<string | number>(-1);
  const [page, setPage] = useState<number>(1);
  const { isMaintenanceMode } = useMaintenanceMode();

  const status = props.status || "active";

  const normalizeKeyPart = (value: unknown): string | null => {
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized.length ? normalized : null;
    }
    if (typeof value === 'number' || typeof value === 'bigint') {
      return String(value).toLowerCase();
    }
    return null;
  };

  const buildParentKey = (platform: unknown, name: unknown, resource: unknown): string | null => {
    const p = normalizeKeyPart(platform);
    const n = normalizeKeyPart(name);
    const r = normalizeKeyPart(resource);
    if (!p || !n || !r) {
      return null;
    }
    return `${p}|${n}|${r}`;
  };

  const getModuleResourceCandidates = (module: PrDescriptionMetaDataModuleEntryEnriched): string[] => {
    const candidates = new Set<string>();
    const rawResourceName = normalizeKeyPart((module as any).resourceName);
    if (rawResourceName) {
      candidates.add(rawResourceName);
    }

    const resolvedResourceName = normalizeKeyPart((module as any).resolvedResourceName);
    if (resolvedResourceName) {
      candidates.add(resolvedResourceName);
    }

    const prefixed = normalizeKeyPart(`${(module as any).parentName?.prefix || ''}${(module as any).resourceName || ''}${(module as any).parentName?.suffix || ''}`);
    if (prefixed) {
      candidates.add(prefixed);
    }

    return Array.from(candidates);
  };

  useEffect(() => {
    const getUserRequests = async () => {
      setLoading(true);
      try {
        const backendApiService = new BackendApiService(accessToken ?? '');
        const userRequests = await backendApiService.getUserRequests(
          status,
          props.sinceDays,
          props.showAllResourcesFilter,
          true,
          true,
          undefined,
          props.windowed
        );
        const parentKeys = new Set<string>();
        userRequests.forEach(resource => {
          resource.modules.forEach(module => {
            const parent = (module as any).parent;
            const parentKey = buildParentKey(parent?.platform, parent?.name, parent?.resource);
            if (parentKey) {
              parentKeys.add(parentKey);
            }
          });
        });

        const userRequestsWithoutParents = userRequests.filter(resource => {
          const moduleMatchesParent = resource.modules.some(module => {
            const platform = normalizeKeyPart(module.platform);
            const name = normalizeKeyPart(module.name);
            if (!platform || !name) {
              return false;
            }

            return getModuleResourceCandidates(module).some(candidateResource =>
              parentKeys.has(`${platform}|${name}|${candidateResource}`)
            );
          });

          return !moduleMatchesParent;
        });
        const userRequestsTransformed = userRequestsWithoutParents.map(r => {
          const modules: UiModule[] = [];
          r.modules.forEach(m => {
            const found = modules.find(m2 => m.platform === m2.platform && m.name === m2.name);
            if (found) {
              found.count += 1;
            } else {
              modules.push({ platform: m.platform, name: m.name, title: m.title, count: 1 });
            }
          });

          return {
            ...r, id: r.modules?.[0].id, platform: r.modules?.[0].platform, name: r.modules?.[0].name, modulesUi: modules
          } as UiResource;
        });
        setUserRequests(userRequestsTransformed);
        setVisibleData(userRequestsTransformed.slice(0, PAGE_SIZE));
        setLoading(false);
      } catch (err) {
        console.log(err);
      }
    };

    getUserRequests();
  }, [accessToken, status, props.sinceDays, props.showAllResourcesFilter, props.windowed]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop !== document.documentElement.offsetHeight || loading || page * PAGE_SIZE >= userRequests.length) {
        return;
      }

      setLoading(true);
      setTimeout(() => {
        setPage(prevPage => prevPage + 1);
        setLoading(false);
      }, 1000);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading]);

  useEffect(() => {
    const newVisibleData = userRequests.slice(0, page * PAGE_SIZE);
    setVisibleData(newVisibleData);
  }, [page, userRequests]);

  return (
    <div>
      <TableContainer>
        <Table sx={{ width: '100%', minWidth: 650, maxWidth: 1600, margin: 'auto', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell width={80}><strong>Request ID</strong></TableCell>
              <TableCell width={500}><strong>Name</strong></TableCell>
              <TableCell><strong>Reviewer(s)</strong></TableCell>
              <TableCell width={250}><strong>Requester</strong></TableCell>
              <TableCell><strong>Requested At</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell width={40}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              (!visibleData.length && !loading) &&
              <TableRow>
                <TableCell colSpan={7} sx={{ borderBottom: 0 }}>
                  No open requests found!
                </TableCell>
              </TableRow>
            }
            {
              (props?.resourceFilter !== "" ? userRequests.filter(request => {
                return `${request.platform} ${request.title}`.toLowerCase().includes(props?.resourceFilter?.toLowerCase() || "") ||
                  request.modulesUi?.some(module => {
                    return `${module.platform} ${module.title}`.toLowerCase().includes(props?.resourceFilter?.toLowerCase() || "")
                  });
              }) : visibleData).map(request => (
                <TableRow
                  hover
                  onMouseEnter={() => {
                    setShowActionId(request.id);               // set id here
                  }}
                  onMouseLeave={() => setShowActionId(-1)}
                  key={`${request.latestPullRequestId}-row`}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>
                    <Chip label={"#" + request.latestPullRequestId} />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {request.modulesUi?.map((module: any) => (
                      // TODO: Show generated ID (and not grouped) -> {module.count} x 
                      <Link
                        key={`${request.latestPullRequestId}-link`}
                        component={RouterLink}
                        to={`/user/requests/${request.latestPullRequestId || request.pullRequestId}`}
                        onClick={isMaintenanceMode ? (event) => event.preventDefault() : undefined}
                        color="inherit"
                        underline={isMaintenanceMode ? "none" : "hover"}
                        sx={{ 
                          cursor: isMaintenanceMode ? 'not-allowed' : 'pointer',
                          pointerEvents: isMaintenanceMode ? 'none' : 'auto',
                          opacity: isMaintenanceMode ? 0.6 : 1
                        }}
                      >
                        <p key={module.platform + module.name} style={{ display: 'flex', alignItems: 'center' }}>
                          <img
                            alt=""
                            src={`/icons/${request.platform}-${request.name}.svg`}
                            style={{ maxWidth: 18, maxHeight: 18 }}
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null; // prevents looping
                              currentTarget.src = "/icons/default.svg";
                            }} />

                          <strong>
                            <span style={{ textTransform: 'capitalize', paddingLeft: '10px' }}>{module.platform}</span>&nbsp;<span>{module.title}</span>
                          </strong>
                        </p>
                      </Link>
                    ))}
                  </TableCell>
                  <TableCell>
                    {request.checks.review.reviewers?.map((reviewer: any) => (
                      <Stack
                        key={`${request.latestPullRequestId}-${reviewer.name}`}
                        direction="row"
                        spacing={1}
                        sx={{ margin: "4px" }}>
                        <Tooltip title={reviewer.vote > 0 ? "Approved" : reviewer.vote === 0 ? "No review yet" : "Rejected"} placement="right" arrow>
                          <Chip sx={{
                            "& .MuiChip-deleteIcon": {
                              cursor: "default"
                            },
                            "&:hover": {
                              "& .MuiChip-deleteIcon": {
                                color: "rgba(0, 0, 0, 0.26)"
                              }
                            }
                          }}
                            color={"default"}
                            avatar={<Avatar src={reviewer.avatar ?
                              `data:${reviewer.avatar?.imageType || ''};base64, ${reviewer.avatar?.imageData || ''}`
                              :
                              "/terraform-avatar.png"
                            } />}
                            deleteIcon={reviewer.vote > 0 ? <CheckCircleIcon /> : reviewer.vote === 0 ? <RadioButtonUncheckedIcon /> : <CancelIcon />}
                            onDelete={reviewer.vote !== 0 ? () => { } : undefined}
                            label={reviewer.name}
                          />
                        </Tooltip>
                      </Stack>
                    ))}
                  </TableCell>
                  <TableCell>
                    <div style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}>
                      {request.requester}
                    </div>

                  </TableCell>
                  <TableCell>
                    {request.status ?
                      (<Tooltip title={request.updatedDate.toLocaleString()} placement="left" arrow>
                        <span>{request.updatedDate.toLocaleDateString()}</span>
                      </Tooltip>)
                      : "N/A"}
                  </TableCell >

                  <TableCell>
                    <RequestStatusChip
                      status={request.status}
                      type={request.statusType}
                      description={request.statusDescription}
                      onClick={(() => {
                        if (!request.status) {
                          return undefined;
                        }
                        const prUrl = buildAdoPrUrl(request.repository?.url, request.latestPullRequestId || request.pullRequestId);
                        return prUrl ? () => window.open(prUrl, '_blank', 'noopener,noreferrer') : undefined;
                      })()}
                    />
                  </TableCell>
                  <TableCell>
                    {(request.status && request.id === showActionId && !isMaintenanceMode) && <ResourceContextMenu request={request}></ResourceContextMenu>}
                  </TableCell>
                </TableRow >
              ))
            }

            {
              loading && [0, 1, 2].map(i => (
                <TableRow
                  key={i}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell><Skeleton height={30} /></TableCell>
                  <TableCell><Skeleton height={30} /></TableCell>
                  <TableCell><Skeleton width={30} height={30} variant="circular" /></TableCell>
                  <TableCell><Skeleton width={30} height={30} variant="circular" /></TableCell>
                  <TableCell><Skeleton height={30} /></TableCell>
                  <TableCell><Skeleton width={30} height={30} variant="circular" /></TableCell>
                  <TableCell><Skeleton width={30} height={30} /></TableCell>
                </TableRow>
              ))
            }

          </TableBody >
        </Table >
      </TableContainer >
    </div >
  );
}

export default UserRequests;
